<main>
  <!-- Hidden button for focus management after printing -->
  <button #focusButton style="position: absolute; left: -9999px; opacity: 0; pointer-events: none" tabindex="-1"></button>

  <header>
    <div class="headline">
      <h1>
        {{ 'Stations_Weight_Station_Detail_Title' | translateWF: 'Weight Station Details' }}
      </h1>
    </div>
    <div class="buttons">
      <!-- Station area indicator -->
      @if (stationArea(); as area) {
        <div class="form-box-row mb-4">
          <div class="flex items-center gap-3 py-3 px-5 bg-gray-200 rounded-lg">
            <i class="pi pi-map-marker text-blue-600 !text-2xl"></i>
            <span class="font-bold text-2xl text-blue-600">{{ area }}</span>
          </div>
        </div>
      }
    </div>
  </header>

  <p-confirmDialog key="wst-dialog" #cd styleClass="wst-confirm-dialog">
    <ng-template #headless let-message let-onAccept="onAccept" let-onReject="onReject">
      <div class="flex flex-col items-center p-8 bg-white rounded-xl w-full max-w-lg mx-auto shadow-xl">
        <div class="rounded-full bg-amber-500 text-white inline-flex justify-center items-center h-24 w-24 -mt-20 shadow-lg">
          <i class="pi pi-exclamation-triangle text-5xl"></i>
        </div>

        <span class="font-bold text-2xl text-center mt-6 mb-2 text-gray-800">
          {{ message.header }}
        </span>

        <p class="text-center text-xl leading-relaxed text-gray-700" [innerHTML]="message.message"></p>

        <div class="flex justify-center gap-6 mt-8 w-full">
          <p-button [label]="'cancel' | translateWF: 'Cancel'" (click)="onReject()" variant="outlined" styleClass="cs-button-touch" />
          <p-button [label]="'save' | translateWF: 'Save'" (click)="onAccept()" [disabled]="blockSection" styleClass="cs-button-touch" />
        </div>
      </div>
    </ng-template>
  </p-confirmDialog>

  <p-confirmDialog styleClass="touch-confirm-dialog">
    <ng-template #message let-message>
      <div class="flex flex-col items-center text-center w-full gap-4 border-b border-surface-200 dark:border-surface-700 pb-4">
        <i [ngClass]="message.icon" class="!text-6xl text-primary-500"></i>
        <p class="text-lg leading-snug">
          <span [innerHTML]="message.message"></span>
        </p>
      </div>
    </ng-template>
  </p-confirmDialog>

  @if (isLoading > 0) {
    <loading-overlay [isLoading]="true"></loading-overlay>
  }

  <div class="content">
    <div class="grid-12">
      <div class="span-12-6">
        <p-panel>
          <p-card>
            <div class="flex flex-col gap-4">
              @if (isFinalWeightMode) {
                <div class="form-box-row">
                  <label>
                    {{ 'Stations_Weight_Station_Detail_Production_Card' | translateWF: 'Production Card' }}
                  </label>
                  <p-autocomplete
                    fluid
                    class="w-full"
                    styleClass="w-full"
                    inputStyleClass="w-full"
                    #productionCardSearch="cslServerSearch"
                    [cslServerSearch]="fetchProductionCards"
                    [suggestions]="productionCardSearch.results()"
                    [formControl]="productionCardControl"
                    [dropdown]="true"
                    [showClear]="true"
                    [forceSelection]="true"
                    optionLabel="productionCardNumber"
                    (onSelect)="onProductionCardSelect($event)"
                    (onClear)="onProductionCardClear()"
                    [placeholder]="'Stations_Weight_Station_Detail_Search_Production_Card' | translateWF: 'Search production card...'"
                  />
                </div>
              }

              <div class="form-box-row">
                <label>
                  {{ searchLabel }}
                </label>
                <input #rollCodeInput pInputText type="text" class="w-full" [formControl]="searchInput" (keydown.enter)="$event.preventDefault(); search()" />
                <p-button [text]="true" [outlined]="true" icon="pi pi-search" (onClick)="search()" />
                <p-button
                  *ngIf="!isBundleMode"
                  class="whitespace-nowrap"
                  [label]="'Stations_Weight_Station_Detail_More_Info' | translateWF: 'More Info'"
                  (onClick)="openRollDetailModal()"
                  [disabled]="!currentRoll()"
                  variant="outlined"
                />

                <!-- Print button for first weight mode -->
                <p-button
                  *ngIf="currentRoll()?.number && blockSaveButton() && !isBundleMode && !isFinalWeightMode"
                  [label]="'Stations_Weight_Station_Detail_Print_QR' | translateWF: 'Print QR'"
                  icon="pi pi-print"
                  (onClick)="printQrCode(currentRoll()?.number ?? '')"
                  class="p-button-primary whitespace-nowrap"
                />

                <!-- Print button for final weight mode -->
                <p-button
                  *ngIf="currentRoll()?.number && blockSaveButton() && !isBundleMode && isFinalWeightMode"
                  [label]="'Stations_Weight_Station_Detail_Print_Final_Label' | translateWF: 'Print Final Label'"
                  icon="pi pi-print"
                  (onClick)="printFinalLabel(currentRoll()?.number ?? '')"
                  class="p-button-primary whitespace-nowrap"
                />
              </div>

              @if (blockSaveButton()) {
                <div class="form-box-row">
                  <label aria-hidden="true"></label>
                  @if (isFinalWeightMode) {
                    <p-message
                      severity="warn"
                      [text]="'Stations_Weight_Station_Detail_Final_Weight_Recorded_Warning' | translateWF: 'This roll already has a final weight recorded'"
                      class="flex-1 min-w-0"
                      icon="pi pi-info-circle"
                    />
                  } @else {
                    <p-message
                      severity="warn"
                      [text]="'Stations_Weight_Station_Detail_Previously_Weighed' | translateWF: 'This roll has previously been weighed by this weight station'"
                      class="flex-1 min-w-0"
                      icon="pi pi-info-circle"
                    />
                  }
                </div>
              }

              @if (currentRoll() && !isBundleMode) {
                <div class="form-box-row">
                  <label>{{ 'Stations_Weight_Station_Detail_Knitting_Order' | translateWF: 'Knitting Order' }}</label>
                  <input pInputText [value]="currentRoll()?.knittingOrderNumber ?? ''" type="text" class="w-full" [disabled]="true" />
                </div>
                <div class="form-box-row">
                  @if (isFinalWeightMode) {
                    <label>{{ 'Stations_Weight_Station_Detail_Latest_Weight' | translateWF: 'Latest Weight' }}</label>
                    <input pInputText [value]="(showValuesInKgs(getLatestStationWeight()) | number: '1.2-2') + ' ' + weightUnit" type="text" class="w-full" [disabled]="true" />
                  } @else {
                    <label>
                      {{ 'Stations_Weight_Station_Detail_Estimated_Weight' | translateWF: 'Estimated Weight' }}
                    </label>
                    <input
                      pInputText
                      [value]="(showValuesInKgs(currentRoll()?.plannedWeight || 0) | number: '1.2-2') + ' ' + weightUnit"
                      type="text"
                      class="w-full"
                      [disabled]="true"
                    />
                  }
                </div>

                <div class="form-box-row">
                  <label>{{ 'Stations_Weight_Station_Detail_Operator_Name' | translateWF: 'Operator Name' }}</label>
                  <p-autocomplete
                    fluid
                    class="w-full"
                    #userSearch="cslServerSearch"
                    [cslServerSearch]="userTypeahead"
                    [suggestions]="userSearch.results()"
                    field="fieldToDisplay"
                    [dropdown]="true"
                    [forceSelection]="false"
                    [formControl]="operatorControl"
                    [placeholder]="'Stations_Weight_Station_Detail_Search_Operator' | translateWF: 'Search operator...'"
                  />
                  <!-- <input pInputText type="text" [value]="currentRoll()?.createdBy" class="w-full" [disabled]="true" /> -->
                </div>

                <div class="form-box-row">
                  <label>{{ 'Greige_Code' | translateWF: 'Greige Code' }}</label>
                  <input pInputText type="text" [value]="currentGreige()?.greigeCode ?? ''" class="w-full" [disabled]="true" />
                </div>

                <div class="form-box-row">
                  <label>{{ 'Stations_Weight_Station_Detail_Machine_Number' | translateWF: 'Machine Number' }}</label>
                  <input pInputText type="text" [value]="currentAsset()?.assetId ?? ''" class="w-full" [disabled]="true" />
                </div>
                <div class="form-box-row">
                  <label>{{ 'Stations_Weight_Station_Detail_Greige_Description' | translateWF: 'Greige Description' }}</label>
                  <input pInputText type="text" [value]="currentGreige()?.longDescription ?? ''" class="w-full" [disabled]="true" />
                </div>
              }
            </div>
          </p-card>
        </p-panel>

        @if (currentBundle() && isBundleMode) {
          <p-panel>
            <app-rolls-display-table [selectedBundle]="currentBundle()" [fabrics]="bundleFabrics()" [isReadOnly]="true" />
          </p-panel>
        }
      </div>

      <div class="span-12-6">
        <p-panel>
          <p-card>
            <div class="mb-3 flex flex-col gap-3">
              <p-message
                *ngIf="showKnittingOrderFinishedAlert && !isFinalWeightMode"
                severity="error"
                [text]="'Stations_Weight_Station_Detail_Knitting_Qty_Exceeded' | translateWF: 'Knitting order quantity has been exceeded.'"
                class="w-full"
                icon="pi pi-exclamation-triangle"
              />
              @if (!isBundleMode && !isFinalWeightMode) {
                <p-panel class="" styleClass="!bg-[#f8f9fa] !border !border-gray-200 !rounded-lg !p-0">
                  <div class="flex items-center px-1 py-0.5">
                    <div class="flex gap-6 text-gray-700">
                      <div class="flex flex-col items-start">
                        <span class="text-xs font-medium">{{ 'Stations_Weight_Station_Detail_Roll_Count' | translateWF: 'Roll Count' }}</span>
                        <span class="text-base font-semibold"> {{ currentKnittingOrder()?.rollsWeighed ?? 0 }}/{{ currentKnittingOrder()?.rollsCreated ?? 0 }} </span>
                      </div>
                      <div class="flex flex-col items-start">
                        <span class="text-xs font-medium">{{
                          showWeightInKgs()
                            ? ('Stations_Weight_Station_Detail_Planned_Kgs' | translateWF: 'Planned Kgs')
                            : ('Stations_Weight_Station_Detail_Planned_Lbs' | translateWF: 'Planned Lbs')
                        }}</span>
                        <span class="text-base font-semibold">
                          {{ showValuesInKgs(this.currentKnittingOrder()?.issuedQuantity ?? 0) | number: '1.2-2' }}
                          /
                          {{ showValuesInKgs(plannedLbs || 0) | number: '1.2-2' }}
                          {{ weightUnit }}
                        </span>
                      </div>
                      <!-- <div class="flex flex-col items-start">
                    <span class="text-xs font-medium">{{ 'Stations_Weight_Station_Detail_Programmed_Lbs' | translateWF: 'Programmed Lbs' }}</span>
                    <span class="text-base font-semibold">
                      {{ this.currentKnittingOrder()?.issuedQuantity ?? 0 | currency: '' : '' : '1.2-2' }}
                      /
                      {{ getFabricCodeProgrammedQuantity(expectedFabricCode) | currency: '' : '' : '1.2-2' }}
                      lbs
                    </span>
                  </div> -->
                      <div class="flex flex-col items-start">
                        <span class="text-xs font-medium">{{
                          showWeightInKgs()
                            ? ('Stations_Weight_Station_Detail_Remaining_Kgs' | translateWF: 'Remaining Kgs')
                            : ('Stations_Weight_Station_Detail_Remaining_Lbs' | translateWF: 'Remaining Lbs')
                        }}</span>
                        <span class="text-base font-semibold">
                          {{ showValuesInKgs(this.remainingLbs) | number: '1.2-2' }}
                          {{ weightUnit }}
                        </span>
                      </div>
                    </div>
                  </div>
                </p-panel>
              }
            </div>
            <div class="weight__control__card">
              <div
                class="weight__feedback"
                [ngClass]="{
                  weight__net: weightValues.NET !== null,
                  weight__gross: weightValues.GROSS !== null && weightValues.NET === null,
                  weight__tare: weightValues.TARE !== null && weightValues.NET === null && weightValues.GROSS === null,
                  weight__unknown: weightValues.UNKNOWN !== null && weightValues.NET === null && weightValues.GROSS === null && weightValues.TARE === null,
                  weight__default: !hasWeightData,
                }"
              >
                <div class="mb-4" *ngIf="!blockSaveButton()">
                  <label class="flex items-center gap-2">
                    <input type="checkbox" [(ngModel)]="manualWeightOverride" [disabled]="blockAutoCompleteInputs" />
                    <span class="font-semibold text-lg">
                      {{ 'Stations_Weight_Station_Detail_Enter_Weight_Manually' | translateWF: 'Enter Weight Manually' }}
                    </span>
                  </label>
                </div>

                @if (blockSaveButton() && isBundleMode && bundleHasBeenWeighed) {
                  <div class="weight__info text-xl font-medium">
                    {{ 'Stations_Weight_Station_Detail_Bundle_Recorded_Weight' | translateWF: 'Bundle Recorded Weight' }}
                    <span class="block text-3xl font-bold">{{ showValuesInKgs(currentBundle()?.actualWeightInLbs || 0) | number: '1.2-2' }} {{ weightUnit }}</span>
                  </div>
                } @else if (blockSaveButton() && currentRoll()?.id) {
                  <div class="weight__info text-xl font-medium">
                    @if (isFinalWeightMode) {
                      {{ 'Stations_Weight_Station_Detail_Final_Weight_Recorded' | translateWF: 'Final Weight Recorded' }}
                      <span class="block text-3xl font-bold">{{ showValuesInKgs(getRollFinalWeight()) | number: '1.2-2' }} {{ weightUnit }}</span>
                    } @else {
                      {{ 'Stations_Weight_Station_Detail_Rolls_Recorded_Weight' | translateWF: 'Rolls Recorded Weight' }}
                      <span class="block text-3xl font-bold">{{ showValuesInKgs(getRollStationWeight()) | number: '1.2-2' }} {{ weightUnit }}</span>
                    }
                  </div>
                } @else {
                  @if (!manualWeightOverride) {
                    <!-- Bundle mode: Always show gross - tare = net calculation (stacked) -->
                    @if (isBundleMode) {
                      <div class="weight__info text-xl font-medium mb-4 flex flex-col items-start">
                        <!-- Gross Weight (from scale) -->
                        <div class="flex items-center gap-3 py-2 self-start justify-start">
                          <span class="text-3xl font-bold" [class.text-gray-400]="!hasWeightData"> {{ showValuesInKgs(netWeight) | number: '1.2-2' }} {{ weightUnit }} </span>
                          <span class="text-lg text-gray-600">{{ 'Stations_Weight_Station_Detail_Gross_Weight' | translateWF: 'Gross Weight' }}</span>
                        </div>

                        <div class="flex items-center gap-3 py-2 self-start justify-start">
                          <p-select [options]="tareOptions" [formControl]="selectedTare" [showClear]="true" class="w-full" styleClass="cs-select-lg" panelStyleClass="cs-select-lg">
                            <ng-template #selectedItem let-option>
                              @if (option) {
                                <span class="text-lg py-1">{{ showValuesInKgs(selectedTareWeightInLbs) | number: '1.2-2' }} {{ weightUnit }} ({{ getTareLabel(option) }}) </span>
                              }
                            </ng-template>
                            <ng-template #item let-option>
                              <span class="text-lg py-2 block">{{ option.weightKg }} {{ weightUnit }} ({{ getTareLabel(option) }} )</span>
                            </ng-template>
                          </p-select>
                          <span class="text-2xl font-bold text-gray-500">−</span>
                        </div>

                        <!-- Divider line with Total label -->
                        <div class="flex items-center gap-2 w-full my-3">
                          <div class="border-b-4 border-gray-600 flex-1"></div>
                          <span class="text-lg font-semibold text-gray-600">{{ 'Stations_Weight_Station_Detail_Total' | translateWF: 'Total' }}</span>
                          <div class="border-b-4 border-gray-600 flex-1"></div>
                        </div>

                        <!-- Net Weight (result) -->
                        <div class="flex items-center gap-3 py-2 bg-green-50 rounded-lg px-4 self-start justify-start">
                          <span class="text-4xl font-bold text-green-600"> {{ showValuesInKgs(netWeightAfterTare) | number: '1.2-2' }} {{ weightUnit }} </span>
                          <span class="text-xl font-semibold text-green-700">{{ 'Stations_Weight_Station_Detail_Net_Weight' | translateWF: 'Net Weight' }}</span>
                        </div>

                        @if (!selectedTare.value && currentBundle()) {
                          <p class="text-orange-500 mt-3 text-lg text-center">
                            {{
                              'Stations_Weight_Station_Detail_Tare_Required'
                                | translateWF
                                  : 'Tare selection is required to
                    save'
                            }}
                          </p>
                        }

                        @if (!hasWeightData) {
                          <p class="text-sm text-gray-500 text-center mt-3">
                            {{ 'Stations_Weight_Station_Detail_Waiting_Scale' | translateWF: 'Waiting for scale data...' }}
                          </p>
                        }
                      </div>
                    } @else if (hasWeightData) {
                      <!-- Non-bundle mode with weight data -->
                      <!-- Main NET weight display (prominent) -->
                      <div class="weight__info text-xl font-medium mb-4">
                        {{ 'Stations_Weight_Station_Detail_Net_Weight' | translateWF: 'NET Weight' }}
                        <span class="block text-3xl font-bold">{{ showValuesInKgs(netWeight) | number: '1.2-2' }} {{ weightUnit }}</span>
                      </div>

                      <!-- All weight values in a compact grid (hide for simple weight mode) -->
                      <div class="grid-2 mb-3" *ngIf="!isSimpleWeightMode">
                        <div class="text-sm" [class.font-bold]="weightValues.UNKNOWN !== null">
                          <span class="text-xs text-gray-500">RAW:</span>
                          <span class="block text-lg">
                            @if (weightValues.UNKNOWN?.weight) {
                              {{ showValuesInKgs(weightValues.UNKNOWN!.weight!) | number: '1.2-2' }} {{ weightUnit }}
                            } @else {
                              --
                            }
                          </span>
                        </div>
                        <div class="text-sm" [class.font-bold]="weightValues.NET !== null">
                          <span class="text-xs text-gray-500">NET:</span>
                          <span class="block text-lg">
                            @if (weightValues.NET?.weight) {
                              {{ showValuesInKgs(weightValues.NET!.weight!) | number: '1.2-2' }} {{ weightUnit }}
                            } @else {
                              --
                            }
                          </span>
                        </div>
                        <div class="text-sm" [class.font-bold]="weightValues.GROSS !== null">
                          <span class="text-xs text-gray-500">GROSS:</span>
                          <span class="block text-lg">
                            @if (weightValues.GROSS?.weight) {
                              {{ showValuesInKgs(weightValues.GROSS!.weight!) | number: '1.2-2' }} {{ weightUnit }}
                            } @else {
                              --
                            }
                          </span>
                        </div>
                        <div class="text-sm" [class.font-bold]="weightValues.TARE !== null">
                          <span class="text-xs text-gray-500">TARE:</span>
                          <span class="block text-lg">
                            @if (weightValues.TARE?.weight) {
                              {{ showValuesInKgs(weightValues.TARE!.weight!) | number: '1.2-2' }} {{ weightUnit }}
                            } @else {
                              --
                            }
                          </span>
                        </div>
                      </div>

                      <div class="weight__status text-sm text-gray-600">
                        {{ 'Stations_Weight_Station_Detail_Current_Status' | translateWF: 'Current Status' }}: {{ currentWeightStatus }}
                      </div>
                    } @else {
                      <!-- Non-bundle mode without weight data -->
                      <div class="weight__info text-xl font-medium">
                        {{
                          'Stations_Weight_Station_Detail_Physical_Scale_Weight'
                            | translateWF
                              : 'Physical Scale Current
                  Weight'
                        }}
                        @if (currentRoll() && currentRoll()?.stationTimeStamps?.length! > 0) {
                          <span class="block text-3xl font-bold">{{ showValuesInKgs(returnWeightOfCurrentStation()) | number: '1.2-2' }} {{ weightUnit }}</span>
                        } @else {
                          <span class="block text-3xl font-bold">0 {{ weightUnit }}</span>
                        }
                      </div>
                    }
                  } @else {
                    <!-- Manual input shown when toggle is active -->
                    @if (isBundleMode) {
                      <!-- Bundle mode manual input with same stacked layout -->
                      <div class="weight__info text-xl font-medium mb-4 flex flex-col items-start">
                        <!-- Gross Weight (manual input) -->
                        <div class="flex items-center gap-3 py-2 self-start justify-start">
                          <input
                            type="number"
                            [(ngModel)]="manualWeightValue"
                            [disabled]="blockAutoCompleteInputs"
                            class="p-3 text-3xl w-48 rounded-lg border border-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                            [placeholder]="'0.00'"
                          />
                          <span class="text-lg text-gray-600">{{ weightUnit }} {{ 'Stations_Weight_Station_Detail_Gross_Weight' | translateWF: 'Gross Weight' }}</span>
                        </div>

                        <div class="flex items-center gap-3 py-2 self-start justify-start">
                          <p-select [options]="tareOptions" [formControl]="selectedTare" [showClear]="true" class="w-full" styleClass="cs-select-lg" panelStyleClass="cs-select-lg">
                            <ng-template #selectedItem let-option>
                              @if (option) {
                                <span class="text-lg py-1">{{ getTareLabel(option) }} ({{ showValuesInKgs(selectedTareWeightInLbs) | number: '1.2-2' }} {{ weightUnit }})</span>
                              }
                            </ng-template>
                            <ng-template #item let-option>
                              <span class="text-lg py-2 block">{{ getTareLabel(option) }} ({{ option.weightKg }} kg)</span>
                            </ng-template>
                          </p-select>
                          <span class="text-2xl font-bold text-gray-500">−</span>
                        </div>

                        <!-- Divider line with Total label -->
                        <div class="flex items-center gap-2 w-full my-3">
                          <div class="border-b-4 border-gray-600 flex-1"></div>
                          <span class="text-lg font-semibold text-gray-600">{{ 'Stations_Weight_Station_Detail_Total' | translateWF: 'Total' }}</span>
                          <div class="border-b-4 border-gray-600 flex-1"></div>
                        </div>

                        <!-- Net Weight (result) -->
                        <div class="flex items-center gap-3 py-2 bg-green-50 rounded-lg px-4 self-start justify-start">
                          <span class="text-4xl font-bold text-green-600"> {{ showValuesInKgs(manualNetWeightAfterTare) | number: '1.2-2' }} {{ weightUnit }} </span>
                          <span class="text-xl font-semibold text-green-700">{{ 'Stations_Weight_Station_Detail_Net_Weight' | translateWF: 'Net Weight' }}</span>
                        </div>

                        @if (!selectedTare.value && currentBundle()) {
                          <p class="text-orange-500 mt-3 text-lg text-center">
                            {{
                              'Stations_Weight_Station_Detail_Tare_Required'
                                | translateWF
                                  : 'Tare selection is required to
                    save'
                            }}
                          </p>
                        }
                      </div>
                    } @else {
                      <!-- Non-bundle mode manual input -->
                      <div class="flex flex-col items-start gap-2">
                        <label for="manualWeight" class="text-xl font-medium">
                          {{
                            showWeightInKgs()
                              ? ('Stations_Weight_Station_Detail_Enter_Weight_Kgs' | translateWF: 'Enter Weight (Kgs)')
                              : ('Stations_Weight_Station_Detail_Enter_Weight' | translateWF: 'Enter Weight (Lbs)')
                          }}
                        </label>
                        <input
                          type="number"
                          id="manualWeight"
                          [(ngModel)]="manualWeightValue"
                          [disabled]="blockAutoCompleteInputs"
                          class="p-4 text-3xl w-full max-w-sm rounded-lg border border-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                          [placeholder]="'Stations_Weight_Station_Detail_Enter_Weight_Placeholder' | translateWF: 'Enter weight manually'"
                        />
                      </div>
                    }
                  }
                }
              </div>

              <!-- Conditional display using @if -->
            </div>
            <p>
              {{ 'Stations_Weight_Station_Detail_Weight_Control' | translateWF: 'Weight control' }}
            </p>
            <div class="flex w-full item-center justify-between gap-2 p-4">
              <label>{{ 'Stations_Weight_Station_Detail_Show_Weight_In_Kgs' | translateWF: 'Show Weight in Kgs' }}</label>
              <p-toggleSwitch [(ngModel)]="showWeightInKgs" />
            </div>
          </p-card>
        </p-panel>

        @if (currentRoll()) {
          <p-panel [header]="'Stations_Weight_Station_Detail_Activity_Logs' | translateWF: 'Activity Logs'">
            <p-card>
              <p-timeline [value]="events">
                <ng-template pTemplate="opposite" let-event>
                  <small class="event__date">{{ event.date }}</small>
                </ng-template>
                <ng-template pTemplate="content" let-event>
                  {{ showValuesInKgs(event.weightValue) | number: '1.2-2' }} {{ weightUnit }}
                  {{ 'Stations_Weight_Station_Detail_At' | translateWF: 'at' }}
                  <span class="user__name">{{ event.asset }}</span>
                  @if (event.weightType !== undefined && event.weightType !== null) {
                    <span class="ml-2">{{ weightTypeLabel(event.weightType) }}</span>
                  }
                </ng-template>
              </p-timeline>
            </p-card>
          </p-panel>
        }
      </div>
    </div>
  </div>

  <footer class="main__actions">
    <p-button [label]="'Cancel' | translateWF: 'Cancel'" (click)="onCancel()" variant="outlined" styleClass="cs-button-touch-lg" />
    <p-button [label]="'Save' | translateWF: 'Save'" (click)="onSave()" [disabled]="!enableSave" styleClass="cs-button-touch-lg" />
  </footer>
</main>
